export interface PendingForecast {
  dataSeriesId: number;
  seriesDisplay: string;
  date: string;
  dataReleaseId?: number;
  userValue: string;
  mniValue?: string;
}

export interface IndicatorStats {
  display: string;
  wins: number;
  losses: number;
  ties: number;
  // Sum/count of (userValue - actual) across resolved forecasts, to surface
  // systematic bias (e.g. "you consistently forecast Employment too high").
  biasSum: number;
  biasCount: number;
}

export interface ForecastGameStats {
  wins: number;
  losses: number;
  ties: number;
  // Signed: positive = consecutive wins, negative = consecutive losses, 0 after a tie.
  currentStreak: number;
  bestStreak: number;
  badges: string[];
  byIndicator: Record<number, IndicatorStats>;
}

export interface StatsSnapshot {
  wins: number;
  losses: number;
  ties: number;
  badges: string[];
}

export interface ForecastGameState {
  // Bounded by design: only unresolved forecasts live here. Once a release
  // publishes its actual, the entry is folded into `stats` and dropped —
  // this keeps the preferences payload a fixed size forever.
  pending: PendingForecast[];
  stats: ForecastGameStats;
  // Snapshot of `stats` as of the last time Overview computed a "what's new
  // since your last visit" recap. Undefined only before the very first visit.
  lastSeen?: StatsSnapshot;
  // User-curated showcase — badge ids the user picked to feature on Overview,
  // capped at MAX_PINNED_BADGES (see forecast-game-service.ts). Distinct from
  // `stats.badges` (everything ever earned): this is only what they chose to show off.
  pinnedBadges?: string[];
}

export function emptyForecastGameState(): ForecastGameState {
  return {
    pending: [],
    stats: {
      wins: 0,
      losses: 0,
      ties: 0,
      currentStreak: 0,
      bestStreak: 0,
      badges: [],
      byIndicator: {},
    },
  };
}

export type ForecastOutcome = 'win' | 'loss' | 'tie';

// A release published its actual value, but there's nothing to score it
// against (MNI never had a forecast for it, or the data was unusable) — it's
// resolved (must not stay stuck in `pending`), but doesn't count as a win,
// loss, or tie against MNI.
export type ForecastResult = ForecastOutcome | 'no-contest';
